// src/components/LegalConsentBanner.jsx

import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { Button as FcButton } from '../ui';

export default function LegalConsentBanner({ session }) {
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (session?.user?.id) {
      checkLegalAcceptance();
    } else {
      setLoading(false);
    }
  }, [session]);

  const checkLegalAcceptance = async () => {
    try {
      // Check both terms and privacy
      const { data: termsAccepted, error: termsError } = await supabase
        .rpc('has_accepted_latest_legal_document', { 
          doc_type: 'terms',
          check_user_id: session.user.id 
        });

      const { data: privacyAccepted, error: privacyError } = await supabase
        .rpc('has_accepted_latest_legal_document', { 
          doc_type: 'privacy',
          check_user_id: session.user.id 
        });

      if (termsError || privacyError) {
        setLoading(false);
        return;
      }

      // Get the actual documents if not accepted
      const pending = [];

      if (!termsAccepted) {
        const { data: termsDocs } = await supabase
          .rpc('get_active_legal_document', { doc_type: 'terms' });
        if (termsDocs && termsDocs.length > 0) {
          pending.push({ ...termsDocs[0], docType: 'terms' });
        }
      }

      if (!privacyAccepted) {
        const { data: privacyDocs } = await supabase
          .rpc('get_active_legal_document', { doc_type: 'privacy' });
        if (privacyDocs && privacyDocs.length > 0) {
          pending.push({ ...privacyDocs[0], docType: 'privacy' });
        }
      }

      setPendingDocuments(pending);
      setNeedsAcceptance(pending.length > 0);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAll = async () => {
    if (pendingDocuments.length === 0) return;

    setAccepting(true);
    try {
      // Accept all pending documents
      for (const doc of pendingDocuments) {
        await supabase.rpc('record_legal_acceptance', { doc_id: doc.id });
      }

      setNeedsAcceptance(false);
      setPendingDocuments([]);
      
      // Optionally show success message
      // notify('Legal documents accepted successfully', 'success');
    } catch (error) {
      // notify('Failed to record acceptance', 'error');
    } finally {
      setAccepting(false);
    }
  };

  const handleViewDocument = (docType) => {
    if (docType === 'terms') {
      navigate('/terms');
    } else if (docType === 'privacy') {
      navigate('/privacy');
    }
  };

  // Don't show anything while loading or if not logged in
  if (loading || !session?.user?.id || !needsAcceptance) {
    return null;
  }

  return (
    <Modal title="Legal Consent Banner"
      isOpen={needsAcceptance} 
      onClose={() => {}} // Can't close without accepting
      allowClose={false}
    >
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-action-primary rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-ui-green">Updated Legal Documents</h2>
            <p className="text-sm text-content-secondary">Please review and accept to continue</p>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <p className="text-content-primary">
            We've updated our legal documents. Please review and accept the following to continue using Footy Coach:
          </p>

          <div className="bg-gray-50 border border-ui-border rounded-lg p-4 space-y-2">
            {pendingDocuments.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-ui-green">
                    {doc.type === 'terms' ? '📋 Terms of Service' : '🔒 Privacy & Cookies Policy'}
                  </p>
                  <p className="text-sm text-content-secondary">
                    {doc.version} • Effective {new Date(doc.effective_date).toLocaleDateString('en-GB')}
                  </p>
                </div>
                <FcButton variant="tertiary" type="submit"
                  onClick={() => handleViewDocument(doc.docType)}
                  className="underline"
                >
                  Read Full Document
                </FcButton>
              </div>
            ))}
          </div>

          <p className="text-sm text-content-secondary italic">
            By clicking "I Accept All" below, you acknowledge that you have read and agree to the updated documents listed above.
          </p>
        </div>

        <div className="flex gap-3">
          <FcButton variant="primary" type="submit"
            onClick={handleAcceptAll}
            disabled={accepting}
            className="flex-1 disabled:cursor-not-allowed"
          >
            {accepting ? 'Recording Acceptance...' : 'I Accept All'}
          </FcButton>
        </div>

        <p className="text-xs text-content-secondary text-center mt-4">
          You must accept these documents to continue using Footy Coach. If you do not accept, please{' '}
          <FcButton variant="tertiary" type="submit"
            onClick={async () => {
              await supabase.auth.signOut();
              navigate('/login');
            }}
            className="underline"
          >
            log out
          </FcButton>.
        </p>
      </div>
    </Modal>
  );
}
